import React, { useMemo, useState } from 'react'
import {
  UxDraft,
  emptyDraft,
  draftFrom,
  toConfig,
  showsDialog,
  countdownChoices,
  countdownLabel,
  normalizeAccent,
  ACCENT_PRESETS,
  PSADT_LANGS,
  TIMEOUT_CHOICES,
  SHIPPED_PSADT_VERSIONS,
  CloseAppsMode,
  DialogStyle,
  LogoSource,
} from '../utils/uxTemplateDraft'
import { plural } from '../utils/plural'
import { createDeploymentTemplate, updateDeploymentTemplate, DeploymentTemplate, previewCommands, CommandPreview } from '../services/api'
import PsadtDialogPreview, { PreviewPhase, type PreviewSampleApp } from './PsadtDialogPreview'
import ConfigBudgetMeter from './ConfigBudgetMeter'
import { Stepper, Toggle, inputCls, previewGrid, useStepper } from './ui'
import { ChevronDownIcon } from '@heroicons/react/24/outline'

/**
 * User-Experience template wizard. Up to FIVE steps, computed from the draft — a silent
 * experience simply has fewer (mock: UxTemplateWizard.tsx:248).
 *
 * APPEARANCE is a real step here. Two different things get called "branding" and only one of them
 * is this:
 *   device-wide branding    — branding_templates -> a signed override at
 *                             %ProgramData%\sap\psadt\config, layer 3, LOCAL WINS, every package
 *                             on the device. Not this wizard.
 *   deployment-time identity — identity.* in config_json, baked onto the install command as
 *                             -DeploymentConfig b64: at publish, per template. THIS wizard,
 *                             verified on a real endpoint in build 271.
 * An earlier comment here claimed the Appearance step was deliberately absent because branding is
 * on-device only. That conflated the two; the decision is reversed and the step is back.
 *
 * All derivation lives in utils/uxTemplateDraft (toConfig), which is parity-tested against the
 * backend validator — so this component only collects the editable fields and never hand-builds
 * config JSON.
 */

type StepKey = 'basics' | 'behaviour' | 'uninstall' | 'appearance' | 'review'
interface WizStep {
  key: StepKey
  label: string
}

/** Order and blurbs follow the mock (its UxTemplateWizard.tsx:425-430): the two modes that show
 *  a dialog first, then the two that do not. The blurb is what makes the choice legible - the
 *  label alone does not say whether anything appears on screen. */
const MODES: { key: CloseAppsMode; label: string; blurb: string }[] = [
  { key: 'notify', label: 'Ask the user to close it',
    blurb: 'Shows a dialog. The user can close the app, and postpone if you allow it.' },
  { key: 'autoClose', label: 'Close it automatically',
    blurb: 'Warns with a countdown, then closes the app and installs.' },
  { key: 'skip', label: 'Skip the install',
    blurb: 'Try again later. Nothing is shown to the user.' },
  { key: 'perform', label: 'Install anyway',
    blurb: 'Closes the app silently, then installs. Nothing is shown.' },
]
/** The same four, worded for a removal (mock :642-647). */
const UNINSTALL_MODES: { key: CloseAppsMode; label: string; blurb: string }[] = [
  { key: 'notify', label: 'Ask the user to close it',
    blurb: 'Shows a dialog. The user closes the app before it is removed.' },
  { key: 'autoClose', label: 'Close it automatically',
    blurb: 'Warns with a countdown, then closes the app and uninstalls.' },
  { key: 'skip', label: 'Skip the uninstall',
    blurb: 'Try again later. Nothing is shown to the user.' },
  { key: 'perform', label: 'Uninstall anyway',
    blurb: 'Closes the app silently, then removes it. Nothing is shown.' },
]
/** The postpone counts the mock offers. Zero is deliberately absent: "allowed, zero times" is a
 *  Defer button that never appears, so it must be unreachable rather than merely warned about.
 *  ONE table for both deployment types — a removal that could be postponed five times but not ten
 *  was a bound on the control, not on anything the runtime does. */
const DEFER_COUNTS = [1, 2, 3, 5, 10] as const
const deferLabel = (n: number) => `${n} time${n > 1 ? 's' : ''}`


const Field: React.FC<{ label: string; hint?: string; children: React.ReactNode }> = ({
  label,
  hint,
  children,
}) => (
  <div className="mb-4">
    <label className="label">{label}</label>
    {children}
    {hint && <p className="mt-1 text-xs text-theme-text-tertiary">{hint}</p>}
  </div>
)

// A count or a duration is a few characters wide. Stretching its box to the container makes the
// form read as though a paragraph were expected. Everything else about it is the shared field, so
// it is that string with the width replaced rather than a second one to keep in step.
const narrowCls = inputCls.replace('w-full', 'w-56 max-w-full')

type Setter = <K extends keyof UxDraft>(k: K, v: UxDraft[K]) => void

/**
 * One deployment type's close-apps experience: which draft fields it writes, and how it is worded.
 *
 * Install and uninstall are the same PSADT welcome dialog with a different deployment type behind
 * it, so they take the same settings — the only honest differences are the words and the defaults.
 * Two sets of field NAMES for one set of settings is the whole reason this is data rather than two
 * copies of the form: the copies drifted, and the removal ended up unable to express the timeout
 * policy or the response wait that the install could.
 */
interface PhaseSpec {
  modes: { key: CloseAppsMode; label: string; blurb: string }[]
  /** "When the app is …" — a removal asks about an app that is STILL running. */
  runningLabel: string
  /** What a postpone delays, in the user's words: "the install" / "the uninstall". */
  subject: string
  postponeHint: string
  /** The non-deferring answer to "if they do nothing", worded as the mode list words it. */
  proceedLabel: string
  progressLabel: string
  progressHint: string
  /** The control below the separator: whether the dialogs above appear on every run, or only when
   *  a watched application is running. Worded per side and per state, because "what happens when
   *  nothing is running" is the whole of what it decides. */
  interactiveLabel: string
  interactiveOn: string
  interactiveOff: string
  /** What the mode's own settings still govern, said out loud: with the control off, a busy user
   *  gets exactly the experience the section above describes. */
  interactiveBusyNote: string
  /** The combination that looks interactive and is not. PSADT shows a close-apps dialog only when
   *  it has something to say — a process to close, or a postpone to offer — so with the progress
   *  window off, no postponement and nothing running, the control changes nothing visible. */
  interactiveNothingToShow: string
  /** The progress window that will not appear, because postponing keeps the deployment on the
   *  timing that resolves to silent when there is nothing to close. */
  progressHiddenWhenIdle: string
  /** Uninstall only. The one state the control cannot be honoured in: a removal is shown on an
   *  idle device by being sent as `autoClose`, which zeroes the postpone count, so a DEFERRABLE
   *  removal cannot have both. */
  interactiveRefused?: string
  silentNote: string
  /** Review step: the row that names the deployment type, and the word every other row of this
   *  side is prefixed with — both sides list the same settings, so the rows have to say which
   *  side they belong to. */
  summaryLead: string
  summaryPrefix: string
  keys: {
    mode: 'mode' | 'uninstallMode'
    countdownSeconds: 'countdownSeconds' | 'uninstallCountdownSeconds'
    forceCountdown: 'forceCountdown' | 'uninstallForceCountdown'
    allowDefer: 'allowDefer' | 'uninstallAllowDefer'
    deferCount: 'deferCount' | 'uninstallDeferCount'
    onTimeout: 'onTimeout' | 'uninstallOnTimeout'
    notifyTimeoutMinutes: 'notifyTimeoutMinutes' | 'uninstallNotifyTimeoutMinutes'
    persistPrompt: 'persistPrompt' | 'uninstallPersistPrompt'
    showProgress: 'showProgress' | 'uninstallShowProgress'
    interactive: 'interactive' | 'uninstallInteractive'
  }
}

const INSTALL_PHASE: PhaseSpec = {
  modes: MODES,
  runningLabel: 'When the app is already running',
  subject: 'the install',
  postponeHint: 'They can delay the install a limited number of times.',
  proceedLabel: 'Install anyway',
  progressLabel: 'Show install progress',
  progressHint: 'A small progress window while it installs.',
  interactiveLabel: 'Interactive',
  interactiveOn:
    'The install stays interactive when the app is not running, so the progress window still '
    + 'appears. The dialog itself needs something to close, or a postpone to offer.',
  interactiveOff:
    'Only a device where the app is in use sees anything — unless a progress window is switched on '
    + 'above, which is shown everywhere.',
  interactiveBusyNote:
    'When the app IS running, the install behaves exactly as the section above describes.',
  progressHiddenWhenIdle:
    'Not shown on a device where the app is not running: postponing is on, so the install is not '
    + 'kept interactive there, and a silent install shows no progress window. Turn Interactive on '
    + 'to show it everywhere, or turn postponing off.',
  interactiveNothingToShow:
    'On a device where the app is not running this still shows nothing: the dialog has nothing to '
    + 'close and no postpone to offer, and the progress window is switched off above.',
  silentNote: 'Nothing is shown to the user, so there is no appearance or wording to configure.',
  summaryLead: 'When installing',
  summaryPrefix: 'Install',
  keys: {
    mode: 'mode',
    countdownSeconds: 'countdownSeconds',
    forceCountdown: 'forceCountdown',
    allowDefer: 'allowDefer',
    deferCount: 'deferCount',
    onTimeout: 'onTimeout',
    notifyTimeoutMinutes: 'notifyTimeoutMinutes',
    persistPrompt: 'persistPrompt',
    showProgress: 'showProgress',
    interactive: 'interactive',
  },
}

const UNINSTALL_PHASE: PhaseSpec = {
  modes: UNINSTALL_MODES,
  runningLabel: 'When the app is still running',
  subject: 'the uninstall',
  postponeHint: 'Usually off for a removal — most teams don’t let users delay it.',
  proceedLabel: 'Uninstall anyway',
  progressLabel: 'Show uninstall progress',
  progressHint: 'A small progress window while it is removed.',
  interactiveLabel: 'Interactive',
  interactiveOn:
    'The removal stays interactive when the app is not running, so the progress window still '
    + 'appears. The dialog itself needs something to close, or a postpone to offer.',
  interactiveOff:
    'Only a device where the app is in use sees anything — unless a progress window is switched on '
    + 'above, which is shown everywhere.',
  interactiveBusyNote:
    'When the app IS running, the removal behaves exactly as the section above describes.',
  progressHiddenWhenIdle:
    'Not shown on a device where the app is not running: postponing is on, so the removal is not '
    + 'kept interactive there, and a silent removal shows no progress window. Turn Interactive on '
    + 'to show it everywhere, or turn postponing off.',
  interactiveNothingToShow:
    'On a device where the app is not running this still shows nothing: the dialog has nothing to '
    + 'close and no postpone to offer, and the progress window is switched off above.',
  interactiveRefused:
    'Not applied while postponing is on. A removal is shown on an idle device by being sent as '
    + '“Close it automatically”, which removes the Defer button — so it is refused rather than '
    + 'taking away a choice you also made. Turn postponing off to use this.',
  silentNote: 'Nothing is shown to the user during the uninstall.',
  summaryLead: 'When uninstalling',
  summaryPrefix: 'Uninstall',
  keys: {
    mode: 'uninstallMode',
    countdownSeconds: 'uninstallCountdownSeconds',
    forceCountdown: 'uninstallForceCountdown',
    allowDefer: 'uninstallAllowDefer',
    deferCount: 'uninstallDeferCount',
    onTimeout: 'uninstallOnTimeout',
    notifyTimeoutMinutes: 'uninstallNotifyTimeoutMinutes',
    persistPrompt: 'uninstallPersistPrompt',
    showProgress: 'uninstallShowProgress',
    interactive: 'uninstallInteractive',
  },
}

/**
 * The close-apps form for ONE deployment type. Rendered twice, from the two specs above.
 *
 * Which controls appear where is the load-bearing part, and it is the same rule on both sides:
 *   - the countdown belongs to the mode that counts down, and is that mode's ONLY timing: the
 *     dialog lives exactly as long as the countdown, so the renderer sizes UI.DefaultTimeout to
 *     fit it (template_service.dialog_timeout_for) rather than asking for a second number;
 *   - the response wait, postponement and the timeout policy are `notify` only, because every
 *     other mode either shows nothing or force-closes on a timer, so none could be honoured.
 */
const CloseAppsFields: React.FC<{ d: UxDraft; set: Setter; phase: PhaseSpec }> = ({
  d,
  set,
  phase,
}) => {
  const k = phase.keys
  const mode = d[k.mode]
  const interactive = d[k.interactive]
  const allowDefer = d[k.allowDefer]
  const onTimeout = d[k.onTimeout]
  const timeoutMinutes = d[k.notifyTimeoutMinutes]
  const countdown = d[k.countdownSeconds]

  /** The dialog's own timeout, paired with the policy beside it — one decision in two halves, so
   *  they share a row. */
  const waitField = (
    <Field
      label="Wait for a response"
      hint="How long the dialog stays on screen before the choice beside it is made for them."
    >
      <select
        className={inputCls}
        value={timeoutMinutes}
        onChange={(e) => set(k.notifyTimeoutMinutes, Number(e.target.value))}
      >
        {TIMEOUT_CHOICES.map((m) => (
          <option key={m} value={m}>
            {m} minutes
          </option>
        ))}
      </select>
    </Field>
  )

  return (
    <>
      <Field label={phase.runningLabel} hint={phase.modes.find((m) => m.key === mode)?.blurb}>
        <select
          className={inputCls}
          value={mode}
          onChange={(e) => set(k.mode, e.target.value as CloseAppsMode)}
        >
          {phase.modes.map((m) => (
            <option key={m.key} value={m.key}>
              {m.label}
            </option>
          ))}
        </select>
      </Field>

      {/* The countdown mode's only timing control (mock :456-468). UI.DefaultTimeout is not
          offered here: the countdown runs inside the dialog and is what ends it, so the renderer
          ships the dialog timeout extended past the countdown whenever the stored wait is shorter
          (template_service.dialog_timeout_for). */}
      {mode === 'autoClose' && (
        <Field label="Countdown before closing" hint="How long the user has to save their work.">
          <select
            className={narrowCls}
            value={countdown}
            onChange={(e) => set(k.countdownSeconds, Number(e.target.value))}
          >
            {countdownChoices(countdown).map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </Field>
      )}

      {/* `notify` only. In autoClose the countdown IS the behaviour: the renderer branches
          on onTimeout for notify alone, so offering these there is a control that changes
          nothing the device does. */}
      {mode === 'notify' && (
        <div className="space-y-3 rounded-lg border border-theme-border-primary bg-theme-primary p-4 mb-4">
          <div className="flex items-center justify-between">
            <div className="pr-4">
              <p className="text-sm font-medium text-theme-text-primary">Let the user postpone</p>
              <p className="text-xs text-theme-text-tertiary">{phase.postponeHint}</p>
            </div>
            <Toggle
              checked={allowDefer}
              onChange={(v) => set(k.allowDefer, v)}
              label={`Let the user postpone ${phase.subject}`}
            />
          </div>

          {/* Only shown when postponing is on. */}
          {allowDefer && (
            <Field label="How many times">
              {/* A bounded choice, not a number field. Free entry let an admin save
                  "postponing allowed, zero times", which renders no Defer button at all -
                  the backend could only warn about it after the fact. */}
              <select
                className={narrowCls}
                value={d[k.deferCount]}
                onChange={(e) => set(k.deferCount, Number(e.target.value))}
              >
                {DEFER_COUNTS.map((n) => (
                  <option key={n} value={n}>{deferLabel(n)}</option>
                ))}
              </select>
            </Field>
          )}

          {/* One decision in two halves — how long to wait, and what to do when the wait runs
              out — so they share a row. */}
          <div className="grid gap-3 sm:grid-cols-2">
            {waitField}
            <Field label="If they do nothing">
              <select
                className={inputCls}
                value={onTimeout}
                onChange={(e) => set(k.onTimeout, e.target.value as 'defer' | 'proceed')}
              >
                <option value="defer">Postpone for them</option>
                <option value="proceed">{phase.proceedLabel}</option>
              </select>
            </Field>
          </div>

          {/* Only meaningful when a countdown runs AND postpones exist to delay it —
              without them the plain countdown starts immediately anyway. */}
          {onTimeout === 'proceed' && allowDefer && (
            <div className="border-t border-theme-border-primary pt-3">
              <div className="flex items-center justify-between">
                <div className="pr-4">
                  <p className="text-sm font-medium text-theme-text-primary">
                    Show the countdown right away
                  </p>
                  <p className="text-xs text-theme-text-tertiary">
                    {d[k.forceCountdown]
                      ? 'The timer is visible immediately, even while they can still postpone.'
                      : 'The timer only appears once their postpones have run out.'}
                  </p>
                </div>
                <Toggle
                  checked={d[k.forceCountdown]}
                  onChange={(v) => set(k.forceCountdown, v)}
                  label="Show the countdown right away"
                />
              </div>
            </div>
          )}

          <div className="border-t border-theme-border-primary pt-3">
            <div className="flex items-center justify-between">
              <div className="pr-4">
                <p className="text-sm font-medium text-theme-text-primary">Make them respond</p>
                <p className="text-xs text-theme-text-tertiary">
                  Keeps re-centring the prompt until the user answers.{' '}
                  {/* The toolkit only persists once deferral is gone or expired, so with
                      postponing off it bites straight away. */}
                  {allowDefer
                    ? 'Only kicks in once their postpones have run out.'
                    : 'Applies immediately, since postponing is off.'}
                </p>
              </div>
              <Toggle
                checked={d[k.persistPrompt]}
                onChange={(v) => set(k.persistPrompt, v)}
                label="Make them respond"
              />
            </div>
          </div>
        </div>
      )}

      {showsDialog(mode) ? (
        <>
          {/* THE PROGRESS WINDOW BELONGS TO THE MODE ABOVE. On a deployment that only acts when the
              app is in use, the window is shown only then — so it is part of that decision, not a
              separate one, and it sits inside the group that states it. */}
          <div className="flex items-center justify-between">
            <div className="pr-4">
              <p className="text-sm font-medium text-theme-text-primary">{phase.progressLabel}</p>
              <p className="text-xs text-theme-text-tertiary">{phase.progressHint}</p>
            </div>
            <Toggle
              checked={d[k.showProgress]}
              onChange={(v) => set(k.showProgress, v)}
              label={phase.progressLabel}
            />
          </div>
          {/* The window that is on and will not appear. Everywhere else a requested window keeps
              the deployment visible; with postponing on it cannot, because doing so would put a
              postpone dialog in front of a user the administrator chose not to interrupt. */}
          {d[k.showProgress] && d[k.allowDefer] && mode === 'notify' && !interactive && (
            <p className="mt-2 text-xs text-amber-500">{phase.progressHiddenWhenIdle}</p>
          )}

          {/* WHAT HAPPENS WHEN NOTHING IS RUNNING, and only that. It used to be inferred from the
              progress toggle, so asking for a window silently turned on an always-prompt and
              nothing said so. Below the separator because it applies to the run as a whole, where
              everything above answers "the app is already running, now what". */}
          <div className="mt-4 border-t border-theme-border-primary pt-3">
            <div className="flex items-center justify-between">
              <div className="pr-4">
                <p className="text-sm font-medium text-theme-text-primary">
                  {phase.interactiveLabel}
                </p>
                <p className="text-xs text-theme-text-tertiary">
                  {interactive ? phase.interactiveOn : phase.interactiveOff}
                </p>
              </div>
              <Toggle
                checked={interactive}
                onChange={(v) => set(k.interactive, v)}
                label={phase.interactiveLabel}
              />
            </div>
            {/* The half an admin cannot see from the toggle: off does not make the deployment
                silent, it makes it silent FOR AN IDLE USER. */}
            {!interactive && (
              <p className="mt-2 text-xs text-theme-text-tertiary">{phase.interactiveBusyNote}</p>
            )}
            {/* And the combination where it is on and still shows nothing. Worth saying out loud:
                it reads as a switch that did not work. */}
            {/* The EFFECTIVE postponement, not the stored one: it exists only in the prompting
                mode, so a countdown mode carries none however the control was last left. Reading
                the raw field suppressed this on exactly the mode that needs it. */}
            {interactive && !d[k.showProgress] && !(mode === 'notify' && allowDefer) && (
              <p className="mt-2 text-xs text-amber-500">{phase.interactiveNothingToShow}</p>
            )}
            {/* And the one combination the runtime cannot honour. A removal is shown on an idle
                device by being sent as `autoClose` — the only field the device derives a removal's
                DeployMode from — and that zeroes the postpone count. Saying nothing here would
                leave an administrator with a switch that silently did nothing. */}
            {interactive && phase.interactiveRefused && mode === 'notify' && d[k.allowDefer] && (
              <p className="mt-2 text-xs text-amber-500">{phase.interactiveRefused}</p>
            )}
          </div>
        </>
      ) : (
        <p className="mt-4 rounded-lg border border-theme-border-primary bg-theme-primary px-4 py-3 text-[13px] text-theme-text-tertiary">
          {phase.silentNote}
        </p>
      )}
    </>
  )
}

/**
 * Preview column with an Install/Uninstall switcher. Which side to look at is a VIEWING choice,
 * not a setting — local state, never part of the draft. Rendered only where at least one side
 * shows a dialog.
 */
const PreviewPane: React.FC<{
  d: UxDraft
  label: string
  sampleApp?: PreviewSampleApp
}> = ({ d, label, sampleApp }) => {
  const installDialog = showsDialog(d.mode)
  const uninstallDialog = showsDialog(d.uninstallMode)
  const both = installDialog && uninstallDialog
  const [side, setSide] = useState<PreviewPhase>(installDialog ? 'install' : 'uninstall')
  // A mode change can silence the selected side — fall back to the one that still has a dialog.
  const effective: PreviewPhase =
    side === 'install'
      ? installDialog
        ? 'install'
        : 'uninstall'
      : uninstallDialog
        ? 'uninstall'
        : 'install'

  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <p className="text-sm font-medium text-theme-text-primary">
          {label}
          {!both && (effective === 'uninstall' ? ' — uninstall' : ' — install')}
        </p>
        {both && (
          <div className="flex overflow-hidden rounded-md border border-theme-border-primary">
            {(['install', 'uninstall'] as const).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setSide(s)}
                className={`px-2 py-1 text-[11px] font-medium capitalize ${
                  effective === s
                    ? 'bg-theme-tertiary text-theme-text-primary'
                    : 'text-theme-text-tertiary hover:text-theme-text-secondary'
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        )}
      </div>
      <PsadtDialogPreview draft={d} phase={effective} sampleApp={sampleApp} />
    </div>
  )
}

export interface UxTemplateWizardProps {
  template?: DeploymentTemplate
  onClose: () => void
  onSaved: () => void
  /**
   * APPLY MODE (spec §7a.1). When set, the wizard does not save: it hands the derived config back
   * to the caller, which carries it as an override on ONE deployment. The template is untouched,
   * so every other app published from it keeps its settings.
   *
   * Basics is dropped in this mode — a name and description belong to a saved template, and there
   * is nothing here to save them under.
   */
  onApply?: (config: any) => void
  /**
   * APPLY MODE only: the caller's apply is an async round trip to Intune. When true the Apply button
   * shows a spinner and is disabled, so the user sees the PATCH is in flight rather than a dead
   * click on a button that looks done.
   */
  applying?: boolean
  /** The app this template is being applied to, drawn in the previews. Omitted when a template is
   *  edited on its own, and the previews then draw 7-Zip. */
  sampleApp?: PreviewSampleApp
}

const UxTemplateWizard: React.FC<UxTemplateWizardProps> = ({
  template,
  onClose,
  onSaved,
  onApply,
  applying = false,
  sampleApp,
}) => {
  const [d, setD] = useState<UxDraft>(() =>
    template
      ? draftFrom(template.config_json, template.name, template.description ?? '')
      : emptyDraft(),
  )
  const [stepIdx, setStepIdx] = useState(0)
  // EDIT mode: every step is reachable - the template already exists, so there is no
  // 'not yet done' step and forcing a linear walk to change one field is just friction.
  // CREATE mode: only steps already VISITED are reachable. Jumping ahead would show a
  // Review built from defaults the user never saw, which reads as though they chose them.
  const isEdit = !!template
  // Tracked by step KEY, not index: the step list changes shape as the modes change, so an index
  // recorded before Appearance appeared would unlock a different step afterwards.
  // Seeded with the FIRST step, which is not 'basics' in apply mode — seeding a key that is not
  // in the list would leave every step unreachable.
  const [visited, setVisited] = useState<Set<StepKey>>(
    () => new Set<StepKey>([onApply ? 'behaviour' : 'basics']),
  )
  const [saving, setSaving] = useState(false)
  const [advOpen, setAdvOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // The generated PSADT command line, on demand. Not fetched on render: it is a server round trip
  // and nobody needs it until they ask, but once asked it must reflect the CURRENT draft rather than
  // the last saved version — which is the whole reason it takes toConfig(d) rather than a template id.
  const [cmdPreview, setCmdPreview] = useState<CommandPreview | null>(null)
  const [cmdLoading, setCmdLoading] = useState(false)
  const [cmdError, setCmdError] = useState<string | null>(null)

  const generateCommands = async () => {
    setCmdLoading(true)
    setCmdError(null)
    try {
      setCmdPreview(await previewCommands(toConfig(d)))
    } catch (e: any) {
      setCmdError(e?.response?.data?.detail || e?.message || 'could not generate the command line')
    } finally {
      setCmdLoading(false)
    }
  }

  // Steps are computed from the draft — a silent experience simply has fewer.
  const steps = useMemo<WizStep[]>(() => {
    const s: WizStep[] = [
      // A name is only worth asking for when something is saved under it — so apply mode, which
      // hands the settings back to one rollout instead of writing a template, has no Basics.
      ...(onApply ? [] : [{ key: 'basics' as StepKey, label: 'Basics' }]),
      { key: 'behaviour', label: 'Install behaviour' },
      { key: 'uninstall', label: 'Uninstall behaviour' },
    ]
    // Appearance is shared by both deployment types — shown if EITHER puts up a dialog, since a
    // template that only prompts on removal still needs its dialog to look right.
    if (showsDialog(d.mode) || showsDialog(d.uninstallMode)) {
      s.push({ key: 'appearance', label: 'Appearance' })
    }
    s.push({ key: 'review', label: 'Review' })
    return s
  }, [d.mode, d.uninstallMode, onApply])

  // Switching to a silent mode can drop Appearance out from under the cursor, leaving stepIdx
  // past the end of the list.
  const safeIdx = Math.min(stepIdx, steps.length - 1)
  const step = steps[safeIdx]
  const isLast = safeIdx === steps.length - 1
  const rail = useStepper(safeIdx)

  const advance = () => {
    const next = Math.min(safeIdx + 1, steps.length - 1)
    setVisited((v) => new Set(v).add(steps[next].key))
    setStepIdx(next)
  }

  // A countdown the wizard picks for the author, always short enough to reach zero before the
  // dialog closes. Four fifths of the wait keeps round numbers round: 5 minutes -> 4.
  const countdownInside = (waitMinutes: number | undefined) =>
    Math.max(30, Math.floor(((waitMinutes ?? 5) * 60 * 4) / 5))

  const set = <K extends keyof UxDraft>(k: K, v: UxDraft[K]) =>
    setD((p) => {
      const nd = { ...p, [k]: v }
      // "Close it automatically" needs a real countdown — 0 (notify's "just wait") would render a
      // blank select and emit a 0-second force-close. It must also finish INSIDE the wait it runs
      // in, or the dialog expires first and the deployment ends at 1618 without installing.
      if (k === 'mode' && nd.mode === 'autoClose' && nd.countdownSeconds === 0) {
        nd.countdownSeconds = countdownInside(nd.notifyTimeoutMinutes)
      }
      if (k === 'uninstallMode' && nd.uninstallMode === 'autoClose' && nd.uninstallCountdownSeconds === 0) {
        nd.uninstallCountdownSeconds = countdownInside(nd.uninstallNotifyTimeoutMinutes)
      }
      // Without defers, force-vs-plain countdown is indistinguishable — the toggle is hidden, so
      // clear the flag lest stale force state leak into the saved config.
      if (k === 'allowDefer' && !nd.allowDefer) {
        nd.forceCountdown = false
      }
      if (k === 'uninstallAllowDefer' && !nd.uninstallAllowDefer) {
        nd.uninstallForceCountdown = false
      }
      return nd
    })

  const canAdvance = step.key !== 'basics' || d.name.trim().length > 0

  /** Jump straight to a step from the rail. Going back never validates, exactly as Back does not;
   *  going forward runs the same rule Continue runs, so a jump cannot skip what the step being
   *  left owns. */
  const goToStep = (i: number) => {
    if (i === safeIdx) return
    if (i > safeIdx && !canAdvance) return
    if (isEdit || visited.has(steps[i].key)) {
      setVisited((v) => new Set(v).add(steps[i].key))
      setStepIdx(i)
    }
  }

  const save = async () => {
    setSaving(true)
    setError(null)
    try {
      const body = {
        name: d.name.trim(),
        description: d.description,
        config_json: toConfig(d),
      }
      if (template) await updateDeploymentTemplate(template.id, body)
      else await createDeploymentTemplate(body)
      onSaved()
      onClose()
    } catch (e: any) {
      // Surface the backend's own reason. Its validator returns {errors, warnings}, and those
      // messages are written to be read by an admin — paraphrasing them loses the detail.
      const detail = e?.response?.data?.detail
      setError(
        typeof detail === 'string'
          ? detail
          : detail?.errors?.join('; ') || e?.message || 'Could not save the template.',
      )
    } finally {
      setSaving(false)
    }
  }

  const languageLabel =
    d.language === 'auto'
      ? 'Auto-detect'
      : (PSADT_LANGS.find((l) => l.code === d.language)?.label ?? d.language)

  return (
    <div>
      {/* No card chrome (border / rounded / raised background): the wizard is a PAGE now, not a
          modal over one, and panel framing at full width just reads as a very large dialog. Fields
          line up with the page header instead of being inset by a card's padding. */}
      <div className="w-full py-2">
        {/* Apply mode is hosted by the deploy wizard, which has its own modal title and its own
            "this deployment only" note — repeating "Edit template" here would say the opposite of
            what apply mode does. */}
        {!onApply && (
          <>
            <h3 className="text-lg font-semibold text-theme-text-primary mb-4">
              {template ? `Edit ${template.name}` : 'New user experience'}
            </h3>
          </>
        )}

        <Stepper
          id={rail.id}
          steps={steps}
          current={safeIdx}
          onGoTo={goToStep}
          /* Editing an existing experience: nothing is "not yet done", so every step is one
             click away. Creating one: only what has been walked through, or a Review assembled
             from defaults nobody saw would read as chosen. */
          canGoTo={isEdit ? undefined : (i) => visited.has(steps[i].key)}
          ariaLabel="User experience steps"
        />

        <div {...rail.panelProps}>
        {step.key === 'basics' && (
          <>
            <Field label="Name">
              <input
                className={inputCls}
                value={d.name}
                onChange={(e) => set('name', e.target.value)}
                placeholder="Laptops — interactive"
              />
            </Field>
            <Field label="Description" hint="Shown in the template list.">
              <input
                className={inputCls}
                value={d.description}
                onChange={(e) => set('description', e.target.value)}
                placeholder="What this experience is for"
              />
            </Field>
          </>
        )}

        {step.key === 'behaviour' && (
          <>
            <CloseAppsFields d={d} set={set} phase={INSTALL_PHASE} />

            {/* Post-install action — applies whatever the mode above is, and the one setting the
                removal has no counterpart for. */}
            <div className="mt-4 flex items-center justify-between rounded-lg border border-theme-border-primary bg-theme-primary px-4 py-3">
              <div>
                <p className="text-sm font-medium text-theme-text-primary">
                  Remove desktop shortcuts
                </p>
                <p className="text-xs text-theme-text-tertiary">
                  Deletes the app's desktop shortcut once the install finishes.
                </p>
              </div>
              <Toggle
                checked={d.removeDesktopShortcuts}
                onChange={(v) => set('removeDesktopShortcuts', v)}
                label="Remove desktop shortcuts"
              />
            </div>
          </>
        )}

        {step.key === 'uninstall' && (
          <>
            <p className="mb-4 rounded-lg border border-theme-border-primary bg-theme-primary px-4 py-2.5 text-xs text-theme-text-tertiary">
              Uninstalling runs as its own PSADT deployment type, so it can behave differently from
              the install. The settings are the same ones — only the defaults differ.
            </p>

            <CloseAppsFields d={d} set={set} phase={UNINSTALL_PHASE} />
          </>
        )}

        {step.key === 'appearance' && (
          <div className={previewGrid}>
            <div>
              <Field
                label="Dialog style"
                hint={
                  d.dialogStyle === 'modern'
                    ? 'The modern Fluent dialog.'
                    : 'The classic PSADT dialog with a banner.'
                }
              >
                <select
                  className={inputCls}
                  value={d.dialogStyle}
                  onChange={(e) => set('dialogStyle', e.target.value as DialogStyle)}
                >
                  <option value="modern">Fluent (modern)</option>
                  <option value="classic">Classic</option>
                </select>
                {d.dialogStyle === 'modern' && (
                  <p className="mt-1.5 rounded-lg border border-amber-500/30 bg-amber-500/[0.07] px-3 py-2 text-xs text-amber-500">
                    Fluent needs .NET Framework 4.7.2 on the device. Windows 10 1803 and later ship
                    it; anything older falls back to the Classic dialog rather than failing.
                  </p>
                )}
              </Field>

              {/* The style decides which asset slot applies. The icon beside each process to close
                  is always the app's own and is not configurable. */}
              {/* IMAGES ARE NOT PART OF A DEPLOYMENT any more, so there is no control here — a
                  statement of where they are authored instead. They used to travel on the Intune
                  install command, which could not hold them: a base64 image does not fit the
                  1024-character limit, and the URL that replaced it made a dialog's appearance
                  depend on the device reaching our storage as SYSTEM at install time. They now
                  arrive with a branding package, which writes them into the device's own
                  configuration. */}
              <Field label="Logo and banner">
                <BrandingSourceNote />
              </Field>

              {d.dialogStyle === 'modern' ? (
                <>
                  <Field
                    label="Accent colour"
                    hint="Colours the left bar, primary button and links in the Fluent dialog."
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      {ACCENT_PRESETS.map((hex) => (
                        <button
                          key={hex}
                          type="button"
                          onClick={() => set('fluentAccent', hex)}
                          title={hex}
                          aria-label={`Accent ${hex}`}
                          className={`h-7 w-7 rounded-full border-2 transition-transform ${
                            normalizeAccent(d.fluentAccent) === hex
                              ? 'scale-110 border-theme-text-primary'
                              : 'border-transparent hover:scale-105'
                          }`}
                          style={{ background: hex }}
                        />
                      ))}
                      <label className="ml-1 flex items-center gap-1.5 text-xs text-theme-text-tertiary">
                        <input
                          type="color"
                          // Lowercase on purpose: <input type="color"> normalizes its value to
                          // lowercase in the DOM, so feeding it the uppercase stored form leaves
                          // React re-writing a value the browser keeps changing back.
                          value={normalizeAccent(d.fluentAccent).toLowerCase()}
                          onChange={(e) => set('fluentAccent', e.target.value)}
                          className="h-6 w-6 cursor-pointer rounded border border-theme-border-primary bg-transparent"
                        />
                        Custom
                      </label>
                    </div>
                  </Field>
                </>
              ) : null}

              {/* Company name, dialog style and accent STAY here — a deployment may set them, and
                  most do. What they cannot do is win against a device that has been branded: the
                  on-device configuration is applied last, so where a branding package sets one of
                  these, it decides. Said here rather than discovered when a dialog looks wrong. */}
              <Field
                label="Company name"
                hint="Shown in the dialog the user sees."
              >
                <input
                  className={inputCls}
                  value={d.companyName}
                  onChange={(e) => set('companyName', e.target.value)}
                />
                <p className="mt-1.5 text-xs text-theme-text-tertiary">
                  A device with branding deployed uses the name that branding sets, where it sets
                  one. This applies everywhere else.
                </p>
                {/* UNDER THE COMPANY NAME, because that is the only field in v2 an author types
                    freely and so the only one that can actually be shortened. Everything else is a
                    fixed-size choice. Silent until the draft is near the limit. */}
                <ConfigBudgetMeter config={toConfig(d)} />
              </Field>

              <Field
                label="Dialog language"
                hint={
                  d.language === 'auto'
                    ? "Follows each user's Windows display language, using PSADT's built-in translations."
                    : 'Every user sees the dialogs in this language. The wording itself is not editable.'
                }
              >
                <select
                  className={inputCls}
                  value={d.language}
                  onChange={(e) => set('language', e.target.value)}
                >
                  <option value="auto">Auto-detect (recommended)</option>
                  {PSADT_LANGS.map((l) => (
                    <option key={l.code} value={l.code}>
                      {l.label}
                    </option>
                  ))}
                </select>
              </Field>

              {/* Classic only: a deployment's balloon tips all come from Show-ADTInstallationProgress
                  and Close-ADTInstallationProgress, and with a Fluent dialog style Show-ADTBalloonTip
                  drops those callers before it reads UI.BalloonNotifications — the toggle can change
                  nothing there, so offering it would promise a notification Fluent never shows. */}
              {d.dialogStyle === 'classic' && (
                <div className="mb-4 rounded-lg border border-theme-border-primary bg-theme-primary p-4">
                  <div className="flex items-center justify-between">
                    <div className="pr-4">
                      <p className="text-sm font-medium text-theme-text-primary">
                        Show balloon notifications
                      </p>
                      <p className="text-xs text-theme-text-tertiary">
                        {d.balloonNotifications
                          ? 'A notification appears when the deployment starts and when it finishes.'
                          : 'Nothing appears in the notification area. Dialogs are unaffected.'}
                      </p>
                    </div>
                    <Toggle
                      checked={d.balloonNotifications}
                      onChange={(v) => set('balloonNotifications', v)}
                      label="Show balloon notifications"
                    />
                  </div>
                </div>
              )}
            </div>
            <PreviewPane d={d} label="Live preview" sampleApp={sampleApp} />
          </div>
        )}

        {step.key === 'review' && (
          <div className={previewGrid}>
            <div className="text-sm text-theme-text-secondary">
              <dl className="space-y-1">
                <Line k="Name" v={d.name || '—'} />
                <PhaseSummary d={d} phase={INSTALL_PHASE} />
                {d.removeDesktopShortcuts && (
                  <Line k="Desktop shortcuts" v="Removed after install" />
                )}
                <PhaseSummary d={d} phase={UNINSTALL_PHASE} />
                {showsDialog(d.mode) && (
                  <>
                    <Line k="Company name" v={d.companyName} />
                    <Line
                      k="Dialog style"
                      v={d.dialogStyle === 'modern' ? 'Fluent (modern)' : 'Classic'}
                    />
                    {/* The review says what this template SHIPS, and it ships no image. Claiming
                        "custom upload" here would describe a tenant asset that no longer travels
                        with a deployment — the review would be reporting something that does not
                        happen, which is worse than reporting nothing. */}
                    <Line k="Logo and banner" v="From branding deployed to the device" />
                    <Line k="Language" v={languageLabel} />
                    {/* Classic only, like the toggle: Fluent never shows the start/finish balloon
                        tips, so the review would be reporting something that does not happen. */}
                    {d.dialogStyle === 'classic' && (
                      <Line
                        k="Tray notifications"
                        v={d.balloonNotifications ? 'Shown at start and finish' : 'Off'}
                      />
                    )}
                  </>
                )}
              </dl>

              <button
                type="button"
                onClick={() => setAdvOpen((a) => !a)}
                className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-medium text-blue-400 hover:underline"
              >
                <ChevronDownIcon
                  className={`h-3.5 w-3.5 transition-transform ${advOpen ? 'rotate-180' : ''}`}
                />
                Advanced
              </button>
              {advOpen && (
                <div className="mt-3 space-y-3 rounded-lg border border-theme-border-primary bg-theme-primary p-4">
                  <Field label="PSADT version">
                    <select
                      className={inputCls}
                      value={d.psadtVersion}
                      onChange={(e) => set('psadtVersion', e.target.value)}
                    >
                      {/* The versions we actually ship. The backend rejects anything else outright
                          (template_service._V2_PSADT_VERSION), so offering more would be a picker
                          whose other options 400 on save. */}
                      {SHIPPED_PSADT_VERSIONS.map((v) => (
                        <option key={v} value={v}>
                          {v}
                        </option>
                      ))}
                    </select>
                  </Field>
                </div>
              )}

              {/* The config above is what we STORE. This is what Intune will actually RUN — and the
                  two are not the same thing to check: the settings ride the command line as base64,
                  and every bug this feature has had was a setting that stopped somewhere between
                  the two. Generated from the CURRENT draft, so it reflects unsaved edits.

                  Read-only server-side: no Graph call, no app, nothing published. */}
              <div className="mt-4 border-t border-theme-border-primary pt-3">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-[12px] text-theme-text-tertiary">
                    PSADT command line — what Intune will run
                  </p>
                  <button
                    type="button"
                    onClick={generateCommands}
                    disabled={cmdLoading}
                    className="rounded-lg border border-theme-border-primary px-2.5 py-1 text-[12px] font-medium text-theme-text-primary hover:bg-theme-tertiary disabled:opacity-50"
                  >
                    {cmdLoading ? 'Generating…' : cmdPreview ? 'Regenerate' : 'Generate'}
                  </button>
                </div>
                {cmdError && <p className="mt-2 text-[12px] text-red-400">{cmdError}</p>}
                {cmdPreview && (
                  <div className="mt-2 space-y-2">
                    <div>
                      <p className="text-[11px] text-theme-text-tertiary">Install</p>
                      <code className="block overflow-x-auto rounded border border-theme-border-primary bg-theme-primary p-2 text-[11px] text-theme-text-primary">
                        {cmdPreview.install_command}
                      </code>
                    </div>
                    <div>
                      <p className="text-[11px] text-theme-text-tertiary">Uninstall</p>
                      <code className="block overflow-x-auto rounded border border-theme-border-primary bg-theme-primary p-2 text-[11px] text-theme-text-primary">
                        {cmdPreview.uninstall_command}
                      </code>
                    </div>
                    <p className="text-[11px] text-theme-text-tertiary">
                      Mode:{' '}
                      <span className="text-theme-text-primary">
                        {cmdPreview.interactive ? 'Interactive' : 'NonInteractive'}
                      </span>
                      {' · '}
                      {/* Called out because it was broken: the uninstall used to publish WITHOUT the
                          config, which made every uninstall-side setting here unreachable. */}
                      Uninstall carries these settings:{' '}
                      <span className={cmdPreview.uninstall_carries_config ? 'text-theme-text-primary' : 'text-amber-400'}>
                        {cmdPreview.uninstall_carries_config ? 'yes' : 'no'}
                      </span>
                      {cmdPreview.config_transport === 'url' && ' · settings offloaded to a blob (too large to inline)'}
                    </p>
                    {cmdPreview.warnings.map((w) => (
                      <p key={w} className="text-[11px] text-amber-400">{w}</p>
                    ))}
                  </div>
                )}
              </div>
            </div>
            {/* What the user will actually see. A mode/countdown/deferral combination reads very
                differently as a dialog than as a settings list — this is the step where "closes
                apps after 300s with 3 deferrals" becomes something an admin can judge. */}
            <div>
              {showsDialog(d.mode) || showsDialog(d.uninstallMode) ? (
                <PreviewPane d={d} label="What the user sees" sampleApp={sampleApp} />
              ) : (
                <>
                  <p className="mb-1.5 text-sm font-medium text-theme-text-primary">
                    What the user sees
                  </p>
                  <div className="flex min-h-[200px] flex-col items-center justify-center rounded-xl border border-dashed border-theme-border-primary text-center">
                    <p className="text-sm font-medium text-theme-text-primary">Nothing</p>
                    <p className="mt-1 max-w-[240px] text-xs text-theme-text-tertiary">
                      This experience is silent — install and uninstall both happen without any
                      dialog.
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
        </div>

        {error && <p className="mt-3 text-xs text-red-400">{error}</p>}

        <div className="flex justify-between mt-6">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-sm text-theme-text-tertiary hover:text-theme-text-primary"
          >
            Cancel
          </button>
          <div className="flex gap-2">
            {safeIdx > 0 && (
              <button
                type="button"
                onClick={() => setStepIdx(safeIdx - 1)}
                className="px-3 py-1.5 text-sm border border-theme-border-primary rounded-lg text-theme-text-secondary"
              >
                Back
              </button>
            )}
            {!isLast ? (
              <button
                type="button"
                disabled={!canAdvance}
                onClick={advance}
                className="px-3 py-1.5 text-sm border border-theme-border-primary rounded-lg text-theme-text-primary disabled:opacity-50"
              >
                Continue
              </button>
            ) : onApply ? (
              // Apply mode: hand the derived config back, save nothing. No name is required
              // because nothing is being stored under one. The apply is an async PATCH to Intune,
              // so the button spins and disables until the caller reports it done (applying).
              <button
                type="button"
                disabled={applying}
                onClick={() => onApply(toConfig(d))}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-theme-border-primary rounded-lg text-theme-text-primary disabled:opacity-50"
              >
                {applying && (
                  <svg className="h-3.5 w-3.5 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.4 0 0 5.4 0 12h4z" />
                  </svg>
                )}
                {applying ? 'Applying…' : 'Apply to this deployment'}
              </button>
            ) : (
              <button
                type="button"
                disabled={saving || !d.name.trim()}
                onClick={save}
                className="px-3 py-1.5 text-sm border border-theme-border-primary rounded-lg text-theme-text-primary disabled:opacity-50"
              >
                {saving ? 'Saving…' : template ? 'Save changes' : 'Create template'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * Where branding images come from, said once so every screen says it the same way.
 *
 * This is a STATEMENT, not a control, and the distinction is the whole point of the change: an
 * image cannot travel with a deployment. Base64 does not fit Intune's 1024-character install
 * command, and the URL that replaced it made a dialog's appearance depend on the device reaching
 * our storage as SYSTEM at install time. Offering a picker here would promise something that does
 * not arrive.
 *
 * Shared because the application deploy flow has to say the same thing, and two hand-written
 * versions of one explanation drift until they contradict each other.
 */
export const BrandingSourceNote: React.FC<{ compact?: boolean }> = ({ compact }) => (
  <div className="text-xs text-theme-text-tertiary">
    <p>
      Images come from a <span className="text-theme-text-secondary">branding package</span> deployed
      to the device, not from this template. Author one under Settings → Branding and deploy it to a
      device group.
    </p>
    {!compact && (
      <p className="mt-1.5">
        Branding is per device, so one image applies to every application on it. A device with no
        branding deployed shows the application&apos;s own icon.
      </p>
    )}
  </div>
)

const Line: React.FC<{ k: string; v: string }> = ({ k, v }) => (
  <div className="flex items-start justify-between gap-4 border-b border-theme-border-primary pb-1.5 last:border-0">
    <dt className="text-theme-text-tertiary">{k}</dt>
    <dd className="text-right font-medium text-theme-text-primary">{v}</dd>
  </div>
)

/** One deployment type's settings on the Review step, from the same spec that rendered its form —
 *  so a setting cannot be editable on a step and missing from the summary of it. */
const PhaseSummary: React.FC<{ d: UxDraft; phase: PhaseSpec }> = ({ d, phase }) => {
  const k = phase.keys
  const mode = d[k.mode]
  const p = phase.summaryPrefix
  return (
    <>
      <Line k={phase.summaryLead} v={phase.modes.find((m) => m.key === mode)?.label ?? mode} />
      {/* THE ANSWER TO "will the user see anything" — the one thing the mode alone does not say. */}
      <Line
        k={`${p} shown`}
        v={
          !showsDialog(mode)
            ? 'Nothing'
            : d[k.interactive]
              ? 'On every run'
              : 'Only when the app is in use'
        }
      />
      {mode === 'autoClose' && (
        <Line k={`${p} countdown`} v={countdownLabel(d[k.countdownSeconds])} />
      )}
      {mode === 'notify' && (
        <>
          <Line k={`${p} wait`} v={`${d[k.notifyTimeoutMinutes]} min`} />
          <Line
            k={`${p} postpone`}
            v={d[k.allowDefer] ? `Up to ${plural(d[k.deferCount], 'time')}` : 'Not allowed'}
          />
          <Line
            k={`${p} if no response`}
            v={d[k.onTimeout] === 'defer' ? 'Postpone for them' : phase.proceedLabel}
          />
          {d[k.onTimeout] === 'proceed' && d[k.allowDefer] && (
            <Line
              k={`${p} countdown`}
              v={d[k.forceCountdown] ? 'Shown right away' : 'After postpones run out'}
            />
          )}
          {d[k.persistPrompt] && (
            <Line k={`${p} prompt`} v="Kept in front until they answer" />
          )}
        </>
      )}
    </>
  )
}

export default UxTemplateWizard
